import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  createPlayer,
  getPlayers,
  createUserProfile,
  saveConsentAgreement,
} from '../firebase';
import Button from './Button';
import Input from './Input';
import PlayerModal from './PlayerModal';
import './Onboarding.css';
import { KiddiFitWordmark } from './Brand';

function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [players, setPlayers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Player create state is handled by PlayerModal
  const [creating, setCreating] = useState(false);
  const [parentPin, setParentPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [consentAgreed, setConsentAgreed] = useState(false);

  // Helper function to render KiddiFit brand name
  const renderKiddiFitBrand = () => <KiddiFitWordmark />;

  // Helper to format player names for dynamic titles
  const formatNames = (names) => {
    if (!names || names.length === 0) return 'Kids';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  };

  const steps = [
    {
      title: (
        <>
          Welcome to <KiddiFitWordmark />
        </>
      ),
      content:
        'Even 10 minutes of workouts and skill sessions a day could make a big difference for sport mastery.',
      emoji: '👋',
    },
    {
      title: 'Audio Coaching for Strength & Skills',
      content: 'There are two types of workouts: Strength or Skills.',
      emoji: '🎯',
    },
    {
      title: 'Add Your Players',
      content: null, // Will be rendered as custom content
      emoji: '🙌',
    },
    {
      title: `Keep ${formatNames(
        players.map((p) => p.playerName).filter(Boolean)
      )} Motivated!`,
      content:
        'Unlock new levels and collect awesome badges by completing workouts! These help them climb the leaderboard and show off their  achievements!',
      emoji: '🏆',
    },
    {
      title: 'Points Bank',
      content:
        'Completed workouts rack up points in a bank. Redeem them anytime for fun rewards like treats, toys, or extra screen time. You set the rules!',
      emoji: '💰',
    },
    {
      title: 'Parent PIN Setup',
      content:
        'Set up a 4-digit PIN to manage point withdrawals from the bank. This keeps the reward system secure and under your control.',
      emoji: '🔒',
    },
    {
      title: 'Safety & Liability Agreement',
      content: null, // Will be rendered as custom content
      emoji: '',
    },
  ];

  // Load players when component mounts
  useEffect(() => {
    if (user) {
      loadPlayers();
    }
  }, [user]);

  const loadPlayers = async () => {
    try {
      const playersData = await getPlayers(user.uid);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const submitCreatePlayer = async ({ name, age, emoji }) => {
    setCreating(true);
    try {
      await createPlayer(user.uid, name, emoji, age);
      setShowCreateForm(false);
      await loadPlayers();
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setParentPin(value);
    setPinError('');
  };

  const handleConfirmPinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setConfirmPin(value);
    setPinError('');
  };

  const validatePin = () => {
    if (parentPin.length !== 4) {
      setPinError('PIN must be 4 digits');
      return false;
    }
    if (parentPin !== confirmPin) {
      setPinError('PINs do not match');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    // Handle PIN setup step (step 5) - only validate PIN here
    if (currentStep === 5) {
      if (!validatePin()) {
        return;
      }
      // Save PIN to user profile
      try {
        await createUserProfile(user.uid, { parentPin });
      } catch (error) {
        console.error('Error saving PIN:', error);
        alert('Failed to save PIN. Please try again.');
        return;
      }
    }

    // Handle consent step (step 6) - ensure consent is agreed
    if (currentStep === 6) {
      if (!consentAgreed) {
        alert(
          'Please read and agree to the Safety & Liability Agreement to continue.'
        );
        return;
      }
      // Save consent agreement to Firebase
      try {
        await saveConsentAgreement(user.uid);
      } catch (error) {
        console.error('Error saving consent agreement:', error);
        alert('Failed to save consent agreement. Please try again.');
        return;
      }
    }

    // For step 2 (Add Your Players), just proceed if players exist
    if (currentStep === 2 && players.length === 0) {
      return; // Don't proceed if no players added
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const currentStepData = steps[currentStep];
  const titleContent = currentStepData.title;

  // Render custom content for "Add Your Players" step
  const renderAddPlayersContent = () => {
    return (
      <div className='add-players-content'>
        <p>
          Start by adding your kids as players. Parents can join as players too
          for a fun family challenge!
        </p>

        {/* Existing Players */}
        {players.length > 0 && (
          <div className='existing-players'>
            <div className='players-list'>
              {players.map((player) => (
                <div key={player.id} className='player-item'>
                  <span className='player-avatar'>{player.avatar || '🐶'}</span>
                  <span className='player-name'>{player.playerName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Player Button */}
        <Button type='primary' onClick={() => setShowCreateForm(true)}>
          Add New Player
        </Button>
      </div>
    );
  };

  // Render custom content for PIN setup step
  const renderPinSetupContent = () => {
    return (
      <div className='pin-setup-content'>
        <p>{currentStepData.content}</p>
        <div className='pin-inputs-container'>
          <div className='pin-input-group'>
            <label>Enter 4-digit PIN:</label>
            <Input
              type='tel'
              value={parentPin}
              onChange={handlePinChange}
              placeholder='0000'
              maxLength={4}
              pattern='[0-9]*'
              inputMode='numeric'
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
            />
          </div>
          <div className='pin-input-group'>
            <label>Confirm PIN:</label>
            <Input
              type='tel'
              value={confirmPin}
              onChange={handleConfirmPinChange}
              placeholder='0000'
              maxLength={4}
              pattern='[0-9]*'
              inputMode='numeric'
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
            />
          </div>
        </div>
        {pinError && <div className='pin-error'>{pinError}</div>}
      </div>
    );
  };

  // Render custom content for consent form step
  const renderConsentContent = () => {
    return (
      <div className='consent-content'>
        <div className='consent-text'>
          <p>
            By using {renderKiddiFitBrand()}, you acknowledge and agree to the
            following terms:
          </p>
          <ul>
            <li>
              <strong>Physical Activity Risk:</strong> You understand that
              physical activity involves inherent risks of injury, including but
              not limited to sprains, strains, falls, and other physical
              injuries.
            </li>
            <li>
              <strong>Supervision:</strong> You agree to supervise children
              during workouts and ensure they perform exercises safely and
              appropriately for their age and fitness level.
            </li>
            <li>
              <strong>Medical Clearance:</strong> You confirm that all
              participants are medically cleared to engage in physical activity
              and have no known medical conditions that would prevent safe
              participation.
            </li>
            <li>
              <strong>Proper Environment:</strong> You agree to provide a safe
              environment free from hazards and with adequate space for physical
              activity.
            </li>
            <li>
              <strong>Liability Release:</strong> You hereby release{' '}
              {renderKiddiFitBrand()}, its developers, and affiliates from any
              liability for injuries, damages, or losses that may occur during
              use of the app.
            </li>
            <li>
              <strong>Assumption of Risk:</strong> You voluntarily assume all
              risks associated with physical activity and use of the{' '}
              {renderKiddiFitBrand()}
              application.
            </li>
          </ul>
          <p>
            <strong>
              By checking the box below, you acknowledge that you have read,
              understood, and agree to these terms.
            </strong>
          </p>
        </div>
        <div className='consent-checkbox'>
          <label>
            <input
              type='checkbox'
              checked={consentAgreed}
              onChange={(e) => setConsentAgreed(e.target.checked)}
            />
            <span>
              I have read and agree to the Safety & Liability Agreement
            </span>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className='onboarding'>
      <div className='onboarding-container'>
        <div className='onboarding-content'>
          <h1>
            {currentStepData.emoji ? (
              <span className='onboarding-emoji' style={{ marginRight: 8 }}>
                {currentStepData.emoji}
              </span>
            ) : null}
            {titleContent}
          </h1>

          {currentStep === 2 ? (
            renderAddPlayersContent()
          ) : currentStep === 5 ? (
            renderPinSetupContent()
          ) : currentStep === 6 ? (
            renderConsentContent()
          ) : (
            <p>{currentStepData.content}</p>
          )}

          <div className='step-indicators'>
            {steps.map((_, index) => (
              <div
                key={index}
                className={`step-dot ${index === currentStep ? 'active' : ''}`}
              />
            ))}
          </div>

          <div className='onboarding-actions'>
            <Button type='tertiary' onClick={handleBack}>
              Back
            </Button>

            <Button
              type='primary'
              onClick={handleNext}
              disabled={
                (currentStep === 5 &&
                  (parentPin.length !== 4 || confirmPin.length !== 4)) ||
                (currentStep === 2 && players.length === 0) ||
                (currentStep === 6 && !consentAgreed)
              }
            >
              {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </div>
      </div>

      {/* Create Player Modal */}
      {showCreateForm && (
        <PlayerModal
          isOpen={showCreateForm}
          mode='create'
          isSubmitting={creating}
          onClose={() => setShowCreateForm(false)}
          onSubmit={submitCreatePlayer}
        />
      )}
    </div>
  );
}

export default Onboarding;
